/**
 * Draft storage utilities
 * Handles storing and retrieving form draft data
 */

const DRAFT_PREFIX = "waritto-draft-";

export interface DraftStorageResult<T> {
  data: T | null;
  hasContent: boolean;
}

/**
 * Get a draft from storage
 */
export function getDraft<T>(key: string): DraftStorageResult<T> {
  const storageKey = `${DRAFT_PREFIX}${key}`;
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) {
      return { data: null, hasContent: false };
    }

    const parsed = JSON.parse(saved) as T;
    const hasContent = checkHasContent(parsed);
    return { data: parsed, hasContent };
  } catch {
    return { data: null, hasContent: false };
  }
}

/**
 * Save a draft to storage
 */
export function saveDraft<T>(key: string, data: T): boolean {
  const storageKey = `${DRAFT_PREFIX}${key}`;
  try {
    const hasContent = checkHasContent(data);
    if (hasContent) {
      localStorage.setItem(storageKey, JSON.stringify(data));
      return true;
    } else {
      // Clear draft if no meaningful content
      localStorage.removeItem(storageKey);
      return false;
    }
  } catch {
    return false;
  }
}

/**
 * Remove a draft from storage
 */
export function removeDraft(key: string): void {
  const storageKey = `${DRAFT_PREFIX}${key}`;
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Ignore errors
  }
}

/**
 * Get all draft keys matching a prefix
 */
export function getDraftKeys(prefix: string): string[] {
  const keys: string[] = [];
  const fullPrefix = `${DRAFT_PREFIX}${prefix}`;

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(fullPrefix)) {
        keys.push(key.replace(DRAFT_PREFIX, ""));
      }
    }
  } catch {
    // Ignore errors
  }

  return keys;
}

/**
 * Clear all drafts matching a prefix
 */
export function clearDrafts(prefix: string): void {
  const keys = getDraftKeys(prefix);
  keys.forEach((key) => {
    removeDraft(key);
  });
}

/**
 * Check if data has meaningful content
 */
function checkHasContent(data: unknown): boolean {
  if (data === null || data === undefined) {
    return false;
  }

  if (typeof data !== "object") {
    return true;
  }

  return Object.values(data as Record<string, unknown>).some((v) => {
    if (typeof v === "string") return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object" && v !== null) return Object.keys(v).length > 0;
    return v !== null && v !== undefined;
  });
}
